import React, { lazy, Suspense, useEffect, useState } from "react";
import type { MeasurementMode } from "@roll1/shared";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
const AoeToolbar = lazy(() => import("./AoeToolbar"));
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Sun,
  CloudSun,
  Moon,
  BoxSelect,
  Lasso,
  ZoomIn,
  Expand,
  Palette,
} from "lucide-react";
import { useI18n } from "../i18n";
import { useToolContext } from "../contexts/ToolContext";
import { useToolOptions } from "../hooks/useToolOptions";
import { usePermissions } from "../hooks/usePermissions";
import { useSessionContext } from "../contexts/SessionContext";
import { useCanvasContext } from "../contexts/CanvasContext";
import type { GlobalIllumination } from "@roll1/shared";
import type {
  ToolOptionsFlyoutPassthrough,
  ToolbarPosition,
  ToolName,
} from "../types";

interface ToolOptionsFlyoutInternalProps extends ToolOptionsFlyoutPassthrough {
  isGM?: boolean;
  side: "left" | "right";
  toolbarPosition: ToolbarPosition;
  toolbarWidth: number;
}

const ToolOptionsFlyout = React.memo(function ToolOptionsFlyout({
  isGM,
  side,
  toolbarPosition,
  toolbarWidth,
  onFogModeChange,
  onToggleReFogMode,
  onFogCoverAll,
  onFogClearAll,
  measurementMode,
  gridSizeFt,
  onMeasurementModeChange,
  onGridSizeFtChange,
  navigationMode,
  onNavigationModeChange,
  onAoeStartPlacement,
  onAoeClearAll,
  fogSystemMode,
  onFogSystemModeChange,
  onOpenSettings,
  onExpandMap,
  onSketchToMap,
}: ToolOptionsFlyoutInternalProps) {
  const { t } = useI18n();
  const { state: toolState, setTool } = useToolContext();
  const { options, setOption } = useToolOptions();
  const permissions = usePermissions();
  const { mp, pages } = useSessionContext();
  const { globalIlluminationRef, dynamicFog } = useCanvasContext();
  const currentTool = toolState.active;

  // Read current GI from page data
  const currentPageData = pages.viewingPageId
    ? pages.getPageData(pages.viewingPageId)
    : null;
  const [giLocal, setGiLocal] = useState<GlobalIllumination | undefined>(
    currentPageData?.globalIllumination,
  );

  // Sync GI local state when page changes
  useEffect(() => {
    const pd = pages.viewingPageId
      ? pages.getPageData(pages.viewingPageId)
      : null;
    setGiLocal(pd?.globalIllumination);
  }, [pages.viewingPageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const globalIllumination = giLocal ??
    currentPageData?.globalIllumination ?? { level: "bright" as const };

  const handleGlobalIlluminationChange = (gi: GlobalIllumination) => {
    const pid = pages.viewingPageId;
    if (!pid) return;
    const pd = pages.getPageData(pid);
    if (pd) pd.globalIllumination = gi;
    globalIlluminationRef.current = gi;
    setGiLocal(gi);
    dynamicFog.invalidateLighting();
    if (mp.session) {
      mp.send({
        type: "page:update-settings",
        pageId: pid,
        changes: { globalIllumination: gi },
      } as any);
    }
  };
  // V006: Drawing tools accessible if GM or drawOnMap permission
  const canDraw = isGM || permissions.drawOnMap;
  const canNote = isGM || permissions.createNotes;

  const snapCheckbox = (
    <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
      <Checkbox
        data-testid="snap-checkbox"
        checked={options.snapToGrid}
        onCheckedChange={(checked) => setOption("snapToGrid", checked === true)}
      />
      {t("toolOptions.snapToGrid")}
    </label>
  );

  const renderOptions = () => {
    switch (currentTool) {
      case "select":
        return snapCheckbox;

      case "erase":
        if (!canDraw) return null;
        return (
          <div className="flex flex-col gap-2" data-testid="erase-options">
            <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
              {t("toolOptions.width")}{" "}
              <input
                type="range"
                min={1}
                max={20}
                value={options.drawWidth}
                onChange={(e) => setOption("drawWidth", Number(e.target.value))}
                className="w-16 accent-primary"
              />
            </label>
          </div>
        );

      case "draw":
        if (!canDraw) return null;
        return (
          <div className="flex flex-col gap-2" data-testid="draw-options">
            <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
              {t("toolOptions.color")}{" "}
              <input
                type="color"
                value={options.drawColor}
                onChange={(e) => setOption("drawColor", e.target.value)}
                className="w-7 h-6 border-none p-0 cursor-pointer bg-transparent"
              />
            </label>
            <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
              {t("toolOptions.width")}{" "}
              <input
                type="range"
                min={1}
                max={20}
                value={options.drawWidth}
                onChange={(e) => setOption("drawWidth", Number(e.target.value))}
                className="w-16 accent-primary"
              />
            </label>
            {snapCheckbox}
          </div>
        );

      case "shape":
        if (!canDraw) return null;
        return (
          <div className="flex flex-col gap-2" data-testid="shape-options">
            <TooltipProvider delayDuration={300}>
              <div className="flex items-center gap-1">
                <label className="text-meta text-muted-foreground">
                  {t("toolOptions.shape")}
                </label>
                {[
                  {
                    type: "rect" as const,
                    icon: "\u25AD",
                    labelKey: "toolOptions.shapeRect" as const,
                  },
                  {
                    type: "circle" as const,
                    icon: "\u25CB",
                    labelKey: "toolOptions.shapeCircle" as const,
                  },
                  {
                    type: "line" as const,
                    icon: "\u2571",
                    labelKey: "toolOptions.shapeLine" as const,
                  },
                ].map((s) => (
                  <Tooltip key={s.type}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 ${options.shapeType === s.type ? "bg-accent text-accent-foreground" : ""}`}
                        onClick={() => setOption("shapeType", s.type)}
                        data-testid={`shape-type-${s.type}`}
                      >
                        {s.icon}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t(s.labelKey)}</TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
            <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
              {t("toolOptions.color")}{" "}
              <input
                type="color"
                value={options.drawColor}
                onChange={(e) => setOption("drawColor", e.target.value)}
                className="w-7 h-6 border-none p-0 cursor-pointer bg-transparent"
              />
            </label>
            <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
              {t("toolOptions.width")}{" "}
              <input
                type="range"
                min={1}
                max={20}
                value={options.drawWidth}
                onChange={(e) => setOption("drawWidth", Number(e.target.value))}
                className="w-16 accent-primary"
              />
            </label>
            {snapCheckbox}
          </div>
        );

      case "fog":
        if (!isGM) return null;
        return (
          <div className="flex flex-col gap-2" data-testid="fog-options">
            <div className="flex items-center gap-1">
              <label className="text-meta text-muted-foreground">
                {t("toolOptions.fogMode")}
              </label>
              {[
                { mode: "brush" as const, icon: "\u25CF" },
                { mode: "rect" as const, icon: "\u25AD" },
                { mode: "polygon" as const, icon: "\u2B20" },
              ].map((m) => (
                <Button
                  key={m.mode}
                  variant="ghost"
                  size="sm"
                  className={`h-7 w-7 p-0 ${options.fogMode === m.mode ? "bg-accent text-accent-foreground" : ""}`}
                  onClick={() => {
                    setOption("fogMode", m.mode);
                    onFogModeChange?.(m.mode);
                  }}
                  data-testid={`fog-mode-${m.mode}`}
                >
                  {m.icon}
                </Button>
              ))}
            </div>
            {(!options.fogMode || options.fogMode === "brush") && (
              <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
                {t("toolOptions.brushSize")} {options.fogBrushSize ?? 70}px{" "}
                <input
                  type="range"
                  min={20}
                  max={300}
                  step={10}
                  value={options.fogBrushSize ?? 70}
                  onChange={(e) =>
                    setOption("fogBrushSize", Number(e.target.value))
                  }
                  data-testid="fog-brush-size-slider"
                  className="w-16 accent-primary"
                />
              </label>
            )}
            <div className="flex items-center gap-1 flex-wrap">
              {/* Кисть «Закрыть туман» — только в manual-режиме: в dynamic
                  закрытие управляется зрением токенов, а нарисованные
                  closings просто ложатся чёрными пятнами поверх карты. */}
              {onToggleReFogMode && fogSystemMode !== "dynamic" && (
                <Button
                  variant={options.reFogMode ? "default" : "ghost"}
                  size="sm"
                  onClick={onToggleReFogMode}
                  data-testid="fog-refog-toggle"
                >
                  {t("toolOptions.reFog")}
                </Button>
              )}
              {fogSystemMode === "dynamic" && (
                <span className="text-label text-muted-foreground" data-testid="fog-dynamic-hint">
                  {t("toolOptions.dynamicFogHint")}
                </span>
              )}
              {onFogClearAll && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="fog-clear-all"
                    >
                      {t("toolOptions.clearAllFog")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t("fog.confirmClearAll.title")}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("fog.confirmClearAll.description")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t("fog.confirm.cancel")}
                      </AlertDialogCancel>
                      <AlertDialogAction onClick={() => onFogClearAll()}>
                        {t("fog.confirm.confirm")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {onFogCoverAll && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="fog-cover-all"
                    >
                      {t("toolOptions.coverAllFog")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t("fog.confirmCoverAll.title")}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("fog.confirmCoverAll.description")}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t("fog.confirm.cancel")}
                      </AlertDialogCancel>
                      <AlertDialogAction onClick={() => onFogCoverAll()}>
                        {t("fog.confirm.confirm")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            {/* Fog system: manual vs dynamic */}
            {onFogSystemModeChange && (
              <div className="flex items-center gap-1">
                <label className="text-meta text-muted-foreground">
                  {t("toolOptions.fogSystem")}
                </label>
                {(
                  [
                    {
                      mode: "manual" as const,
                      labelKey: "toolOptions.fogManual" as const,
                    },
                    {
                      mode: "dynamic" as const,
                      labelKey: "toolOptions.fogDynamic" as const,
                    },
                  ] as const
                ).map((m) => (
                  <Button
                    key={m.mode}
                    variant="ghost"
                    size="sm"
                    className={`h-7 text-meta ${fogSystemMode === m.mode ? "bg-accent text-accent-foreground" : ""}`}
                    onClick={() => onFogSystemModeChange(m.mode)}
                    data-testid={`fog-system-${m.mode}`}
                  >
                    {t(m.labelKey)}
                  </Button>
                ))}
              </div>
            )}
            {/* Global Illumination */}
            <div className="flex items-center gap-1">
              <label className="text-meta text-muted-foreground">
                {t("page.globalIllumination")}
              </label>
              {(
                [
                  {
                    level: "bright" as const,
                    icon: Sun,
                    label: t("page.illuminationBright"),
                  },
                  {
                    level: "dim" as const,
                    icon: CloudSun,
                    label: t("page.illuminationDim"),
                  },
                  {
                    level: "dark" as const,
                    icon: Moon,
                    label: t("page.illuminationDark"),
                  },
                ] as const
              ).map(({ level, icon: Icon, label }) => {
                const active = globalIllumination?.level === level;
                return (
                  <Button
                    key={level}
                    variant="ghost"
                    size="icon"
                    className="w-7"
                    style={
                      active
                        ? {
                            background: "var(--color-primary)",
                            color: "var(--color-primary-foreground)",
                          }
                        : undefined
                    }
                    onClick={() =>
                      handleGlobalIlluminationChange({
                        level,
                        color: globalIllumination?.color,
                      })
                    }
                    title={label}
                  >
                    <Icon size={14} />
                  </Button>
                );
              })}
            </div>
            {snapCheckbox}
          </div>
        );

      case "measure":
        return (
          <div className="flex flex-col gap-2">
            {snapCheckbox}
            {/* D-36 п.4: the ruler's metric and the grid's feet are campaign settings — the GM's alone (settings:update is gm). */}
            {isGM && (
              <>
              <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
                {t("toolOptions.mode")}{" "}
                <select
                  value={measurementMode ?? "simple"}
                  onChange={(e) =>
                    onMeasurementModeChange?.(e.target.value as MeasurementMode)
                  }
                  className="bg-background text-foreground border border-border rounded px-1 py-0.5 text-ui"
                >
                  <option value="simple">{t("toolOptions.modeSimple")}</option>
                  <option value="alternating">
                    {t("toolOptions.modeAlternating")}
                  </option>
                  <option value="euclidean">
                    {t("toolOptions.modeEuclidean")}
                  </option>
                  <option value="hex">{t("toolOptions.modeHex")}</option>
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
                {t("toolOptions.gridSizeFt")}{" "}
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={gridSizeFt ?? 5}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (v > 0 && v <= 1000) onGridSizeFtChange?.(v);
                  }}
                  className="bg-panel-2 text-fg border border-line rounded px-1 py-0.5 text-ui w-16"
                />
              </label>
              </>
            )}
            {isGM && onOpenSettings && (
              <button
                className="text-meta text-muted-foreground hover:text-foreground underline text-left"
                onClick={() => onOpenSettings("campaign")}
                data-testid="all-settings-link"
              >
                {t("toolOptions.allSettings")}
              </button>
            )}
          </div>
        );

      case "location-link":
        if (!isGM) return null;
        return (
          <TooltipProvider delayDuration={300}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1">
                <label className="text-meta text-muted-foreground">
                  {t("toolOptions.shape")}
                </label>
                {[
                  {
                    mode: "rect" as const,
                    icon: "\u25AD",
                    labelKey: "toolOptions.shapeRect" as const,
                  },
                  {
                    mode: "polygon" as const,
                    icon: "\u2B20",
                    labelKey: "toolOptions.shapePolygon" as const,
                  },
                  {
                    mode: "circle" as const,
                    icon: "\u25CB",
                    labelKey: "toolOptions.shapeCircle" as const,
                  },
                ].map((m) => (
                  <Tooltip key={m.mode}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 ${options.linkDrawMode === m.mode ? "bg-accent text-accent-foreground" : ""}`}
                        onClick={() => setOption("linkDrawMode", m.mode)}
                        data-testid={`link-mode-${m.mode}`}
                      >
                        {m.icon}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t(m.labelKey)}</TooltipContent>
                  </Tooltip>
                ))}
              </div>
              <label className="flex items-center gap-1.5 text-meta text-muted-foreground">
                {t("toolOptions.navigation")}{" "}
                <select
                  value={navigationMode ?? "free"}
                  onChange={(e) =>
                    onNavigationModeChange?.(
                      e.target.value as "free" | "confirm" | "gm-only",
                    )
                  }
                  className="bg-background text-foreground border border-border rounded px-1 py-0.5 text-ui"
                  data-testid="navigation-mode-select"
                >
                  <option value="free">{t("toolOptions.navFree")}</option>
                  <option value="confirm">{t("toolOptions.navConfirm")}</option>
                  <option value="gm-only">{t("toolOptions.navGmOnly")}</option>
                </select>
              </label>
              {snapCheckbox}
            </div>
          </TooltipProvider>
        );

      case "text":
        if (!isGM) return null;
        return (
          <div className="flex flex-col gap-2" data-testid="text-options">
            {snapCheckbox}
          </div>
        );

      case "note":
        if (!canNote) return null;
        return (
          <div className="flex flex-col gap-2" data-testid="note-options">
            {snapCheckbox}
          </div>
        );

      case "aoe":
        if (!isGM) return null;
        return (
          <Suspense fallback={null}>
            <AoeToolbar
              onStartPlacement={onAoeStartPlacement ?? (() => {})}
              onClearAll={onAoeClearAll ?? (() => {})}
            />
          </Suspense>
        );

      case "map-object":
        return snapCheckbox;

      case "wall":
        if (!isGM) return null;
        return (
          <div className="flex flex-col gap-2" data-testid="wall-options">
            <div className="flex items-center gap-1 flex-wrap">
              <label className="text-meta text-muted-foreground">
                {t("toolOptions.wallType")}
              </label>
              {(
                [
                  {
                    type: "wall" as const,
                    color: "#9CA3AF",
                    labelKey: "toolOptions.wallTypeWall" as const,
                  },
                  {
                    type: "door" as const,
                    color: "#F59E0B",
                    labelKey: "toolOptions.wallTypeDoor" as const,
                  },
                  {
                    type: "window" as const,
                    color: "#3B82F6",
                    labelKey: "toolOptions.wallTypeWindow" as const,
                  },
                  {
                    type: "terrain" as const,
                    color: "#22C55E",
                    labelKey: "toolOptions.wallTypeTerrain" as const,
                  },
                  {
                    type: "secret" as const,
                    color: "#A855F7",
                    labelKey: "toolOptions.wallTypeSecret" as const,
                  },
                  {
                    type: "ethereal" as const,
                    color: "#6B7280",
                    labelKey: "toolOptions.wallTypeEthereal" as const,
                  },
                ] as const
              ).map((wt) => (
                <Button
                  key={wt.type}
                  variant="ghost"
                  size="sm"
                  className={`h-7 px-2 text-meta ${options.wallBlockingType === wt.type ? "bg-accent" : ""}`}
                  style={{ color: wt.color }}
                  onClick={() => setOption("wallBlockingType", wt.type)}
                  data-testid={`wall-type-${wt.type}`}
                >
                  {t(wt.labelKey)}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant={options.wallMode !== 'connect' ? 'default' : 'outline'} onClick={() => setOption('wallMode', 'draw')}>{t('walls.draw')}</Button>
              <Button size="sm" variant={options.wallMode === 'connect' ? 'default' : 'outline'} onClick={() => setOption('wallMode', 'connect')}>{t('walls.connect')}</Button>
            </div>
            <p className="max-w-64 text-meta text-muted-foreground">{t(options.wallMode === 'connect' ? 'walls.connectHint' : 'walls.drawHint')}</p>
            {snapCheckbox}
          </div>
        );

      case "inpaint-rect":
      case "inpaint-lasso":
      case "detail-rect":
        if (!isGM) return null;
        return (
          <div className="flex flex-col gap-2" data-testid="ai-edit-options">
            <TooltipProvider delayDuration={300}>
              <div className="flex items-center gap-1">
                <label className="text-meta text-muted-foreground">
                  {t("toolOptions.aiEditMode")}
                </label>
                {(
                  [
                    {
                      tool: "inpaint-rect" as ToolName,
                      icon: BoxSelect,
                      labelKey: "toolbar.inpaintRect" as const,
                    },
                    {
                      tool: "inpaint-lasso" as ToolName,
                      icon: Lasso,
                      labelKey: "toolbar.inpaintLasso" as const,
                    },
                    {
                      tool: "detail-rect" as ToolName,
                      icon: ZoomIn,
                      labelKey: "toolbar.detailRect" as const,
                    },
                  ] as const
                ).map((m) => (
                  <Tooltip key={m.tool}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 ${currentTool === m.tool ? "bg-accent text-accent-foreground" : ""}`}
                        onClick={() => setTool(m.tool)}
                        data-testid={`ai-edit-mode-${m.tool}`}
                      >
                        <m.icon size={14} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t(m.labelKey)}</TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </TooltipProvider>
            <div className="w-full h-px bg-border" />
            <div className="flex flex-col gap-1">
              {onExpandMap && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start gap-1.5"
                  onClick={onExpandMap}
                  data-testid="ai-edit-expand-map"
                >
                  <Expand size={14} />
                  {t("outpaint.expandMap")}
                </Button>
              )}
              {onSketchToMap && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start gap-1.5"
                  onClick={onSketchToMap}
                  data-testid="ai-edit-sketch-to-map"
                >
                  <Palette size={14} />
                  {t("toolOptions.sketchToMap")}
                </Button>
              )}
            </div>
          </div>
        );

      case "token":
        return null;

      default:
        return null;
    }
  };

  const renderedOptions = renderOptions();
  if (!renderedOptions) return null;

  // Position the flyout next to the toolbar
  const flyoutLeft =
    side === "right"
      ? toolbarPosition.x + toolbarWidth + 8
      : toolbarPosition.x - FLYOUT_WIDTH - 8;

  return (
    <div
      className="floating-toolbar-flyout"
      style={{
        position: "fixed",
        left: Math.max(4, flyoutLeft),
        top: toolbarPosition.y,
        zIndex: 'var(--z-panel)',
        width: FLYOUT_WIDTH,
      }}
      data-testid="tool-options-flyout"
    >
      {renderedOptions}
    </div>
  );
});

// 236, not 260: with the Studio plan at 250 px (prototype width) the map pane next to a pinned pane at 1280 is 304 px wide; the options must end inside it.
const FLYOUT_WIDTH = 236;

export default ToolOptionsFlyout;
